import React from 'react';
import transcriptionStartChangelog from './transcription-start.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { googleLanguageOptions, telnyxLanguageOptions } from './languages';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  TranscriptionStartNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Transcription Start',
  identifier: 'TranscriptionStart',
  command_id: 'call.transcription',
  icon: 'telnyx',
  background: '#000000',
  changelog: transcriptionStartChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.transcription.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.transcription.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.transcription',
      label: 'call.transcription',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.transcription.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.transcription',
      expected: false,
      multi: true,
    },
  ],
};

export const state: TranscriptionStartNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  transcription_engine: { type: NodeInputType.Option, value: 'A' },
  language_telnyx: 'en',
  language_google: 'en',
  transcription_tracks: { type: NodeInputType.Option, value: 'inbound' },
  interrupt_playback: { type: NodeInputType.Option, value: 'false' },
  interim_results: { type: NodeInputType.Option, value: 'false' },
  client_state: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'language_telnyx',
    type: NodeFieldType.Option,
    options: telnyxLanguageOptions,
    label: 'Telnyx Transcription Language',
    description: 'Language to use for speech recognition.',
    required: false,
    placeholder: 'Language to use for speech recognition, default English.',
  },
  {
    id: 'language_google',
    type: NodeFieldType.Option,
    options: googleLanguageOptions,
    label: 'Google Transcription Language',
    description: 'Language to use for speech recognition.',
    required: false,
    placeholder: 'Language to use for speech recognition, default English.',
  },
  {
    id: 'transcription_tracks',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Transcription Tracks',
    description:
      'Indicates which leg of the call will be transcribed. Use inbound for the leg that requested the transcription, outbound for the other leg, and both for both legs of the call. Will default to inbound.',
    required: false,
    options: [
      { label: 'Inbound', value: 'inbound' },
      { label: 'Outbound', value: 'outbound' },
      { label: 'Both', value: 'both' },
    ],
  },
  {
    id: 'interrupt_playback',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    options: [
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
    label: 'Interrupt Playback',
    description:
      'Only available with Google transcription engine. When enabled, any playback actively running on the call will be stopped immediately when a new transcription is received to this node. You can use this to implement interruptions when the end user speaks over an actively running playback.',
    required: false,
  },
  {
    id: 'interim_results',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Interim Results',
    description:
      'Whether to send interim results. If set to false, only final results will be sent. Applies to google engine only.',
    required: false,
    placeholder: 'If set to false, only final results will be sent.',
  },
  {
    id: 'transcription_engine',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Transcription Engine',
    description: 'Engine to use for speech recognition.',
    required: false,
    options: [
      { label: 'Google', value: 'A' },
      { label: 'Telnyx', value: 'B' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.transcription': {
      call: {
        transcription: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          transcription_data: {
            confidence: 'number',
            is_final: 'true | false',
            transcript: 'string',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const TranscriptionStart: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.transcription') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
